import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as RKeyboardEvent, type MouseEvent as RMouseEvent, type PointerEvent as RPointerEvent, type ReactNode } from 'react';
import {
  Trash2, Copy, Upload, ChevronUp, ChevronDown, Undo2, Redo2, Eye, EyeOff, Lock, Unlock,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal,
  Grid3x3, Smartphone, Magnet, Maximize2, ChevronsUp, ChevronsDown, GripVertical, Image as ImageIcon, Layers as LayersIcon,
  Type, Square, Star, ClipboardPaste,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import DesignCanvas from './DesignCanvas';
import PhotoFrames, { FrameToolbar, type Frame } from './PhotoFrames';
import {
  BG_LAYER, BLENDS, COLOUR_TOKENS, FADE_SIDES, FONT_SLOTS, FONT_LABEL, FORMATS, ICONS, IMAGE_ROLES, ROLES, adaptTemplate, autoImage, autoText, bgCrop, colourOf,
  iconGlyph, isBrand, moveLayer, newElement, roleRule, snapBox, uploadKitFile, withNewElement,
  type Blend, type BrandKit, type Crop, type Element, type FadeSide, type Guide, type KitPerson, type Lang, type Template,
} from '@/lib/brand-kit';
import { cn } from '@/lib/cn';

const PURPOSES = [
  { value: 'thumbnail', label: 'Reel cover thumbnail' }, { value: 'feed_post', label: 'Feed post' }, { value: 'story', label: 'Story' },
  { value: 'doctor_intro', label: 'Doctor intro' }, { value: 'announcement', label: 'New doctor announcement' },
  { value: 'service', label: 'Service card' }, { value: 'one_pager', label: 'Specialties one-pager' }, { value: 'before_after', label: 'Before and after' },
  { value: 'custom', label: 'Custom' },
];
export const ROLE_LABEL: Record<string, string> = {
  headline: 'Headline', subheadline: 'Subheadline', body: 'Body text', caption: 'Caption', cta: 'Call to action', name: "Doctor's name",
  title: 'Licensed title', specialties: 'Specialties', contact: 'Contact line', signature: 'Signature line', disclaimer: 'Disclaimer',
  photo: "Doctor's photo", image: 'Image', logo: 'Logo', shape: 'Colour block', custom: 'Free text',
  before_photo: 'Before photo', after_photo: 'After photo', label: 'Tag (Before / After)', icon: 'Icon or symbol',
  background: 'Background',
};
const tokenOpts = (empty?: string) => [...(empty ? [{ value: '', label: empty }] : []), ...COLOUR_TOKENS.map((c) => ({ value: c, label: c }))];
const BRAND_TIP = 'Brand asset: only managers and people with Brand details change this';
const SHARED_TIP = 'Shared with you to look at. Use a copy to change it.';
const MARGIN = 64;          // the safe margin every design keeps from the edges, in canvas px
const HISTORY = 80;
const SIDE_LABEL: Record<FadeSide, string> = {
  bottom: 'Toward the bottom', top: 'Toward the top', left: 'Toward the left', right: 'Toward the right',
  bottom_right: 'Toward bottom right', bottom_left: 'Toward bottom left', top_right: 'Toward top right', top_left: 'Toward top left',
};
/** roles the renderer rotates */
const ROTATES = ['shape', 'photo', 'image', 'before_photo', 'after_photo', 'icon'];
type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_POS: Record<Handle, [number, number]> = { nw: [0, 0], n: [50, 0], ne: [100, 0], e: [100, 50], se: [100, 100], s: [50, 100], sw: [0, 100], w: [0, 50] };
const CURSOR: Record<Handle, string> = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize' };

/** Copy and paste layers, within a template and between templates (the browser keeps a copy so
 *  another template, or a reload, still pastes). */
let clipboard: Element[] = [];
const CLIP_KEY = 'kaf.brand.layers.clipboard';
function readClip(): Element[] {
  if (clipboard.length) return clipboard;
  try { const v = JSON.parse(localStorage.getItem(CLIP_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}
function writeClip(els: Element[]) {
  clipboard = structuredClone(els);
  try { localStorage.setItem(CLIP_KEY, JSON.stringify(clipboard)); } catch { /* fine */ }
}

export const layerName = (e: Element) => e.name?.trim() || ROLE_LABEL[e.role] || e.role;

function IconBtn({ title, onClick, active, children, disabled, testid }: { title: string; onClick: () => void; active?: boolean; children: ReactNode; disabled?: boolean; testid?: string }) {
  return (
    <button title={title} aria-label={title} onClick={onClick} disabled={disabled} data-testid={testid}
      className={cn('h-8 w-8 inline-flex items-center justify-center rounded border text-muted hover:text-white disabled:opacity-30',
        active ? 'border-accent-400 text-white bg-accent-500/15' : 'border-slate-800')}>
      {children}
    </button>
  );
}

const Num = ({ label, value, onChange, step, min, max }: { label: string; value: number | undefined; onChange: (v: number) => void; step?: number; min?: number; max?: number }) => (
  <Input type="number" label={label} step={step} min={min} max={max} value={value ?? 0} onChange={(e) => onChange(Number(e.target.value) || 0)} />
);

/** A slider with its value beside it: 0..1 shown as a percentage, or a plain number with a unit. */
function Slider({ label, value, onChange, min = 0, max = 1, step = 0.01, unit, testid, className, title }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; unit?: string; testid?: string; className?: string; title?: string;
}) {
  const shown = unit === undefined ? `${Math.round(value * 100)}%` : `${Math.round(value * 100) / 100}${unit}`;
  return (
    <label className={cn('flex flex-col gap-1 text-xs text-muted', className)} title={title}>
      <span className="flex justify-between gap-2"><span>{label}</span><span className="tabular-nums text-muted-strong" data-testid={testid ? `${testid}-value` : undefined}>{shown}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} aria-label={label} data-testid={testid}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-sky-400" />
    </label>
  );
}

/** One panel section, with an optional "More" toggle for the advanced controls. */
function Section({ title, children, more, testid }: { title: string; children?: ReactNode; more?: ReactNode; testid?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-3 border-t border-slate-800 first:border-t-0 first:pt-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wide text-muted">{title}</h4>
        {more && <button className="text-xs text-accent-400 hover:underline" onClick={() => setOpen(!open)} data-testid={testid ? `${testid}-more` : undefined}>{open ? 'Less' : 'More'}</button>}
      </div>
      {children}
      {more && open && <div className="mt-3 grid gap-2">{more}</div>}
    </div>
  );
}

/** The gradual fade of one layer or of the background: none, toward a side, or from the centre. */
function FadeControls({ fade, side, start, end, onChange, testid }: {
  fade: '' | 'linear' | 'radial'; side: FadeSide; start: number; end: number; testid: string;
  onChange: (p: { fade?: '' | 'linear' | 'radial'; side?: FadeSide; start?: number; end?: number }) => void;
}) {
  return (
    <Section title="Fade" testid={testid} more={fade ? (
      <>
        <Slider label="Visible where the fade starts" value={start} onChange={(v) => onChange({ start: v })} testid={`${testid}-start`} />
        <Slider label="Visible where it ends" value={end} onChange={(v) => onChange({ end: v })} testid={`${testid}-end`} />
      </>
    ) : undefined}>
      <div className="grid grid-cols-2 gap-2">
        <Select value={fade} onChange={(v) => onChange({ fade: v as '' | 'linear' | 'radial' })} data-testid={`${testid}-type`}
          options={[{ value: '', label: 'No fade' }, { value: 'linear', label: 'Fade toward a side' }, { value: 'radial', label: 'Fade from the centre out' }]} />
        {fade === 'linear' && <Select value={side} onChange={(v) => onChange({ side: v as FadeSide })} data-testid={`${testid}-side`}
          options={FADE_SIDES.map((s) => ({ value: s, label: SIDE_LABEL[s] }))} />}
      </div>
      {fade && <p className="text-[11px] text-muted mt-1">Fades this layer only. More sets how strong.</p>}
    </Section>
  );
}

function BlendSelect({ value, onChange, testid, compact }: { value: Blend; onChange: (v: Blend) => void; testid: string; compact?: boolean }) {
  const tip = BLENDS.find((b) => b.value === value)?.tip;
  return (
    <div title={tip}>
      <Select size={compact ? 'sm' : 'md'} value={value} onChange={(v) => onChange(v as Blend)} data-testid={testid}
        options={BLENDS.map((b) => ({ value: b.value, label: b.label }))} />
      {!compact && <p className="text-[11px] text-muted mt-1">{tip}. How this layer mixes with the layers beneath it.</p>}
    </div>
  );
}

/** A small picture of what the layer is, in its colour and opacity. */
function Thumb({ kit, e, lang }: { kit: BrandKit; e: Element; lang: Lang }) {
  const c = colourOf(kit, (e.role === 'shape' || e.role === 'cta' ? e.fill : e.colour) || 'text');
  const box = 'w-6 h-6 shrink-0 rounded border border-white/15 inline-flex items-center justify-center text-[11px] font-semibold bg-slate-200 text-slate-700';
  const style = { opacity: Math.max(0.25, e.opacity ?? 1) };
  if (e.role === 'shape') return <span className={box} style={style}><span className="w-4 h-4 rounded-sm" style={{ background: c }} /></span>;
  if (e.role === 'icon') { const g = iconGlyph(e, lang); return <span className={box} style={{ ...style, color: c }}>{g ? g.slice(0, 2) : <Star size={13} />}</span>; }
  if (e.role === 'logo') return <span className={box} style={style}>L</span>;
  if (IMAGE_ROLES.includes(e.role)) return <span className={box} style={style}><ImageIcon size={13} /></span>;
  return <span className={box} style={{ ...style, color: c }}><Type size={13} /></span>;
}

/** A box element for the background photo, so the photo frame tools can move and zoom it. */
const bgFrameEl = (t: Template) => ({ id: BG_LAYER, role: 'image', x: 0, y: 0, w: t.w, h: t.h, fit: 'cover', radius: 0 } as Element);

interface Drag {
  mode: 'move' | 'size' | 'rotate'; handle?: Handle; sx: number; sy: number; scale: number; pushed: boolean;
  id: string; start: Record<string, Element>;
}

/** One template on the canvas, edited like a light Photoshop (owner 2026-10-24): layers you can
 *  select (click = top-most, Alt+click = the one beneath, Shift+click adds), restack (drag rows,
 *  Bring to front / Send to back, Ctrl+] / Ctrl+[), fade, blend, rename, hide, lock, copy and paste;
 *  the background is the bottom layer. The parent decides which template this is and where it saves.
 *  `canLock` (Brand details) alone chooses brand asset or flexible per element; without it a brand
 *  element cannot be selected, moved, restacked, resized, restyled, faded, hidden, duplicated or
 *  deleted, and a brand background stays as it is. The server enforces the same rule.
 *  `readOnly` shows a design someone shared: look, do not touch. */
export default function TemplateEditor({ clientId, kit, people, template: t, onEdit, onReplace, historyKey, canLock = true, readOnly = false,
  onDuplicate, onAdapt, onDelete, banner }: {
  clientId: string; kit: BrandKit; people: KitPerson[]; template: Template;
  onEdit: (fn: (t: Template) => void) => void; onReplace: (t: Template) => void; historyKey: string;
  canLock?: boolean; readOnly?: boolean;
  onDuplicate?: (t: Template) => void; onAdapt?: (t: Template) => void; onDelete?: () => void; banner?: ReactNode;
}) {
  const [sels, setSels] = useState<string[]>([]);
  const [lang, setLang] = useState<Lang>('en');
  const [busy, setBusy] = useState(false);
  const [snap, setSnap] = useState(true);
  const [grid, setGrid] = useState(false);
  const [safe, setSafe] = useState(true);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [wide, setWide] = useState(false);
  const [thumb, setThumb] = useState('');
  const [sizes, setSizes] = useState<Record<string, { w: number; h: number }>>({});
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);   // display slot (0 = front) the dragged layer would land in
  const [renaming, setRenaming] = useState<string | null>(null);
  const live = useRef<HTMLCanvasElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const rows = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const past = useRef<Template[]>([]);
  const future = useRef<Template[]>([]);
  const lastRec = useRef(0);
  const [, bump] = useState(0);
  const flip = lang === 'ar' && t.mirror_ar !== false;
  /** a box that keeps its side (before and after cards) is not mirrored in the Arabic preview */
  const flipOf = (e?: Element) => flip && !e?.keep_side;
  const viewX = (e: Element) => (flipOf(e) ? t.w - e.x - e.w : e.x);
  const sel = sels.length ? sels[sels.length - 1] : null;
  const setSel = (id: string | null) => setSels(id ? [id] : []);
  const bgSel = sel === BG_LAYER;
  const el = bgSel ? undefined : t.elements.find((e) => e.id === sel);
  const selected = t.elements.filter((e) => sels.includes(e.id));
  const multi = selected.length > 1;
  const person = people.find((p) => p.info.shown !== false);
  const frozen = (e?: Element) => !!e && (readOnly || (!canLock && isBrand(kit, e)));
  const elFrozen = frozen(el);
  const bgFrozen = readOnly || (!canLock && roleRule(kit, 'background') === 'brand');
  const deny = () => toast.error(readOnly ? SHARED_TIP : BRAND_TIP);
  /** selectable at all: a brand asset is not, for the team; on a shared design everything is (to look) */
  const pickable = (e: Element) => !e.hidden && (readOnly || !frozen(e));

  /** Every change goes through here so undo and redo see it. */
  const edit = useCallback((fn: (t: Template) => void, record = true) => {
    if (readOnly) return;
    if (record) { past.current = [...past.current.slice(-HISTORY), structuredClone(t)]; future.current = []; bump((n) => n + 1); }
    onEdit(fn);
  }, [t, onEdit, readOnly]);
  /** Sliders, nudges and in-frame drags change many times a second: one undo step per gesture. */
  const gesture = () => { const now = Date.now(); const r = now - lastRec.current > 700; lastRec.current = now; return r; };
  const undo = useCallback(() => {
    const prev = past.current.pop(); if (!prev) return;
    future.current.push(structuredClone(t)); onReplace(prev); bump((n) => n + 1);
  }, [t, onReplace]);
  const redo = useCallback(() => {
    const next = future.current.pop(); if (!next) return;
    past.current.push(structuredClone(t)); onReplace(next); bump((n) => n + 1);
  }, [t, onReplace]);
  useEffect(() => { past.current = []; future.current = []; setSels([]); bump((n) => n + 1); }, [historyKey]);

  /** Change the selected layer(s); brand assets refuse. */
  const upd = (patch: Partial<Element>, record = true) => {
    const ids = (multi ? selected : el ? [el] : []).filter((e) => !frozen(e)).map((e) => e.id);
    if (!ids.length) { deny(); return; }
    edit((tt) => { for (const e of tt.elements) if (ids.includes(e.id)) Object.assign(e, patch); }, record);
  };
  const updT = (patch: Partial<Template>, record = true) => edit((tt) => { Object.assign(tt, patch); }, record);
  const updBg = (patch: Partial<Template>, record = true) => bgFrozen ? deny() : updT(patch, record);
  const movable = () => (multi ? selected : el ? [el] : []).filter((e) => !frozen(e));
  /** a copy the team may own: element-level brand choices are managers' alone */
  const asCopy = (e: Element, i: number, dx = 24): Element => ({ ...structuredClone(e), id: `d${Date.now().toString(36)}${i}${Math.random().toString(36).slice(2, 5)}`,
    x: e.x + dx, y: e.y + dx, ...(canLock ? {} : { asset: '' as const, locked: false }) });
  const duplicate = () => {
    const src = movable(); if (!src.length) { if (el || multi) deny(); return; }
    const copies = src.map((e, i) => asCopy(e, i));
    if (copies.some((c) => frozen(c))) { deny(); return; }
    edit((tt) => { for (const [i, c] of copies.entries()) { const at = tt.elements.findIndex((x) => x.id === src[i].id); tt.elements.splice(at + 1, 0, c); } });
    setSels(copies.map((c) => c.id));
  };
  const copy = () => { const src = multi ? selected : el ? [el] : []; if (!src.length) return; writeClip(src); toast.success(`${src.length} layer${src.length > 1 ? 's' : ''} copied`); };
  const paste = () => {
    if (readOnly) return;
    const src = readClip(); if (!src.length) { toast.message('Nothing copied yet'); return; }
    const copies = src.map((e, i) => asCopy(e, i, 32));
    if (copies.some((c) => frozen(c))) { deny(); return; }
    edit((tt) => { tt.elements = [...tt.elements, ...copies]; });
    setSels(copies.map((c) => c.id));
  };
  const remove = () => {
    const ids = movable().map((e) => e.id); if (!ids.length) { if (el) deny(); return; }
    edit((tt) => { tt.elements = tt.elements.filter((x) => !ids.includes(x.id)); }); setSels([]);
  };
  /** Restack one layer. Brand assets keep their place; everything else moves past them freely. */
  const restack = (id: string, to: 'front' | 'back' | 'up' | 'down' | number) => {
    const e = t.elements.find((x) => x.id === id);
    if (!e) return;
    if (frozen(e)) { deny(); return; }
    edit((tt) => { tt.elements = moveLayer(tt.elements, id, to); });
  };
  /** The selection's restack, in an order that keeps the selected layers' own stacking. */
  const restackSel = (to: 'front' | 'back' | 'up' | 'down') => {
    const ids = movable().map((e) => e.id); if (!ids.length) { if (el) deny(); return; }
    edit((tt) => {
      const order = tt.elements.map((x) => x.id).filter((x) => ids.includes(x));
      const seq = to === 'front' || to === 'down' ? order : [...order].reverse();
      for (const id of seq) tt.elements = moveLayer(tt.elements, id, to);
    });
  };
  const add = (role: (typeof ROLES)[number]) => {
    const n = newElement(role, t);
    edit((tt) => { tt.elements = withNewElement(tt.elements, n); }); setSel(n.id);
  };
  /** Align: one layer to the canvas margins; several to their shared bounds (as on screen). */
  const align = (how: 'l' | 'c' | 'r' | 't' | 'm' | 'b') => {
    const items = movable(); if (!items.length) { if (el) deny(); return; }
    const m = MARGIN;
    const box = items.length > 1
      ? { l: Math.min(...items.map(viewX)), r: Math.max(...items.map((e) => viewX(e) + e.w)), t: Math.min(...items.map((e) => e.y)), b: Math.max(...items.map((e) => e.y + e.h)) }
      : { l: m, r: t.w - m, t: m, b: t.h - m };
    const ids = items.map((e) => e.id);
    edit((tt) => {
      for (const e of tt.elements) {
        if (!ids.includes(e.id)) continue;
        let vx = viewX(e);
        if (how === 'l') vx = box.l; if (how === 'r') vx = box.r - e.w; if (how === 'c') vx = Math.round((box.l + box.r - e.w) / 2);
        if (how === 't') e.y = box.t; if (how === 'b') e.y = box.b - e.h; if (how === 'm') e.y = Math.round((box.t + box.b - e.h) / 2);
        e.x = Math.round(flipOf(e) ? t.w - vx - e.w : vx);
      }
    });
  };
  /** Space three or more layers evenly, across or down. */
  const distribute = (axis: 'x' | 'y') => {
    const items = movable(); if (items.length < 3) return;
    const pos = (e: Element) => (axis === 'x' ? viewX(e) : e.y), size = (e: Element) => (axis === 'x' ? e.w : e.h);
    const sorted = [...items].sort((a, b) => pos(a) - pos(b));
    const first = pos(sorted[0]), last = pos(sorted[sorted.length - 1]) + size(sorted[sorted.length - 1]);
    const gap = (last - first - sorted.reduce((s, e) => s + size(e), 0)) / (sorted.length - 1);
    const at: Record<string, number> = {}; let cur = first;
    for (const e of sorted) { at[e.id] = Math.round(cur); cur += size(e) + gap; }
    edit((tt) => { for (const e of tt.elements) if (e.id in at) { if (axis === 'y') e.y = at[e.id]; else e.x = flipOf(e) ? t.w - at[e.id] - e.w : at[e.id]; } });
  };
  /** The next layer beneath the selected one that overlaps it (the panel always reaches every layer). */
  const selectBelow = () => {
    const front = [...t.elements].reverse().filter(pickable);
    if (!front.length) return;
    const i = el ? front.findIndex((x) => x.id === el.id) : -1;
    const overlaps = (o: Element) => !el || (viewX(o) < viewX(el) + el.w && viewX(el) < viewX(o) + o.w && o.y < el.y + el.h && el.y < o.y + o.h);
    const next = front.slice(i + 1).find(overlaps) ?? front.slice(i + 1)[0] ?? front[0];
    setSel(next.id);
  };

  // keyboard: nudge, delete, duplicate, copy / paste, undo / redo, restack, escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement)?.isContentEditable) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && k === 'y') { e.preventDefault(); redo(); return; }
      if (mod && k === 'v') { e.preventDefault(); paste(); return; }
      if (e.key === 'Escape') { setSels([]); return; }
      if (!el && !multi) return;
      if (mod && k === 'c') { e.preventDefault(); copy(); return; }
      if (mod && k === 'd') { e.preventDefault(); duplicate(); return; }
      if (mod && (e.code === 'BracketRight' || e.key === ']' || e.key === '}')) { e.preventDefault(); restackSel(e.shiftKey ? 'front' : 'up'); return; }
      if (mod && (e.code === 'BracketLeft' || e.key === '[' || e.key === '{')) { e.preventDefault(); restackSel(e.shiftKey ? 'back' : 'down'); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); remove(); return; }
      const step = e.shiftKey ? 10 : 1;
      const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
      if (dx || dy) {
        e.preventDefault();
        const ids = movable().filter((x) => !x.pinned).map((x) => x.id); if (!ids.length) { deny(); return; }
        edit((tt) => { for (const x of tt.elements) if (ids.includes(x.id)) { x.x += flipOf(x) ? -dx : dx; x.y += dy; } }, gesture());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const preview = useMemo(() => {
    const texts: Record<string, string> = {}; const images: Record<string, string> = {};
    for (const e of t.elements) {
      if (IMAGE_ROLES.includes(e.role)) images[e.id] = autoImage(kit, e, { lang, person });
      else if (e.role !== 'icon') texts[e.id] = autoText(kit, e, { lang, person }) || (e.role === 'shape' ? '' : `${ROLE_LABEL[e.role]}`);
    }
    return { texts, images };
  }, [t, kit, lang, person]);

  // ---------------------------------------------------------------- canvas pointer: select, move, resize, rotate
  const toCanvas = (cx: number, cy: number) => {
    const r = wrap.current!.getBoundingClientRect();
    return { x: (cx - r.left) * t.w / r.width, y: (cy - r.top) * t.h / r.height, scale: t.w / r.width };
  };
  const hitsAt = (x: number, y: number) => [...t.elements].reverse()
    .filter((e) => pickable(e) && x >= viewX(e) && x <= viewX(e) + e.w && y >= e.y && y <= e.y + e.h);
  const startDrag = (ev: RPointerEvent, mode: Drag['mode'], id: string, handle?: Handle) => {
    const p = toCanvas(ev.clientX, ev.clientY);
    const ids = mode === 'move' ? (sels.includes(id) ? sels : [id]) : [id];
    const start: Record<string, Element> = {};
    for (const e of t.elements) if (ids.includes(e.id) && !frozen(e) && !(mode === 'move' && e.pinned)) start[e.id] = { ...e };
    if (!Object.keys(start).length) return;
    drag.current = { mode, handle, sx: p.x, sy: p.y, scale: p.scale, pushed: false, id, start };
    try { wrap.current?.setPointerCapture(ev.pointerId); } catch { /* fine */ }
  };
  const onCanvasDown = (ev: RPointerEvent) => {
    if (bgSel) return;                                  // the background photo frame handles the pointer
    ev.preventDefault();
    const p = toCanvas(ev.clientX, ev.clientY);
    const hits = hitsAt(p.x, p.y);
    if (!hits.length) { if (!ev.shiftKey) setSels([]); return; }
    if (ev.altKey) {
      // Alt+click: the layer underneath the selected one, round and round
      const i = hits.findIndex((x) => x.id === sel);
      setSel(hits[(i + 1) % hits.length].id);
      return;
    }
    const top = hits[0];
    if (ev.shiftKey) { setSels((s) => (s.includes(top.id) ? s.filter((x) => x !== top.id) : [...s.filter((x) => x !== BG_LAYER), top.id])); return; }
    if (!sels.includes(top.id)) setSel(top.id);
    if (!readOnly) startDrag(ev, 'move', top.id);
  };
  const onCanvasMove = (ev: RPointerEvent) => {
    const d = drag.current; if (!d) return;
    const p = toCanvas(ev.clientX, ev.clientY);
    const dx = p.x - d.sx, dy = p.y - d.sy;
    if (!d.pushed) { if (Math.hypot(dx, dy) < 1) return; past.current = [...past.current.slice(-HISTORY), structuredClone(t)]; future.current = []; d.pushed = true; bump((n) => n + 1); }
    let g: Guide[] = [];
    edit((tt) => {
      const base = d.start[d.id] ?? Object.values(d.start)[0];
      if (!base) return;
      if (d.mode === 'move') {
        // snap the layer you grabbed; the rest of the selection moves by the same amount
        let ox = dx, oy = dy;
        if (snap && !ev.altKey) {
          const vt = flip ? { ...tt, elements: tt.elements.map((o) => (o.keep_side ? o : { ...o, x: tt.w - o.x - o.w })) } : tt;
          const bx = viewX(base);
          const r = snapBox(vt, base.id, bx + dx, base.y + dy, base.w, base.h, MARGIN, 10 * d.scale);
          ox = r.x - bx; oy = r.y - base.y; g = r.guides;
        }
        for (const x of tt.elements) {
          const s0 = d.start[x.id]; if (!s0) continue;
          x.x = Math.round(s0.x + (flipOf(s0) ? -ox : ox)); x.y = Math.round(s0.y + oy);
        }
      } else if (d.mode === 'size') {
        const x = tt.elements.find((q) => q.id === d.id); if (!x) return;
        const h = d.handle!;
        let vx = viewX(base), y = base.y, w = base.w, hh = base.h;
        if (h.includes('e')) w = base.w + dx;
        if (h.includes('w')) w = base.w - dx;
        if (h.includes('s')) hh = base.h + dy;
        if (h.includes('n')) hh = base.h - dy;
        w = Math.max(20, w); hh = Math.max(20, hh);
        // from a corner, photos and icons keep their shape; Shift swaps that for any layer
        const keep = h.length === 2 && (ev.shiftKey !== (IMAGE_ROLES.includes(base.role) || base.role === 'icon'));
        if (keep) { const s = Math.max(w / base.w, hh / base.h); w = base.w * s; hh = base.h * s; }
        if (h.includes('w')) vx = viewX(base) + base.w - w;
        if (h.includes('n')) y = base.y + base.h - hh;
        x.w = Math.round(w); x.h = Math.round(hh); x.y = Math.round(y);
        x.x = Math.round(flipOf(base) ? tt.w - vx - w : vx);
      } else {
        const x = tt.elements.find((q) => q.id === d.id); if (!x) return;
        const cx = viewX(base) + base.w / 2, cy = base.y + base.h / 2;
        let deg = Math.atan2(p.y - cy, p.x - cx) * 180 / Math.PI + 90;
        if (deg > 180) deg -= 360;
        if (Math.abs(deg) < 3) deg = 0;
        x.rotation = Math.round(Math.max(-45, Math.min(45, deg)));
      }
    }, false);
    setGuides(g);
  };
  const onCanvasUp = () => { drag.current = null; setGuides([]); };

  const bgUpload = async (f?: File) => {
    if (!f) return;
    if (bgFrozen) { deny(); return; }
    setBusy(true);
    try { const p = await uploadKitFile(clientId, 'background', f); updBg({ background: p, bg_scale: 1, bg_offset_x: 0, bg_offset_y: 0 }); toast.success('Background uploaded'); }
    catch (e) { toast.error((e as Error).message || 'Upload failed'); } finally { setBusy(false); }
  };
  const fmt = FORMATS.find((f) => f.w === t.w && f.h === t.h)?.key ?? '';
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;
  const story = t.h / t.w > 1.6;
  const isText = el && !IMAGE_ROLES.includes(el.role) && el.role !== 'shape' && el.role !== 'icon';

  // the background photo as a photo frame: drag to move, wheel or pinch to zoom (like Generate)
  const bgCropNow: Crop = bgCrop(t);
  const setBgCrop = (c: Crop) => updBg({ bg_scale: c.zoom, bg_offset_x: c.x, bg_offset_y: c.y }, gesture());
  const bgFrame: Frame | null = t.background ? { el: bgFrameEl(t), label: 'Background photo', has: true, adjust: !!sizes[BG_LAYER] && !bgFrozen, busy } : null;

  // layers: front first, then the background (always last, never above an element)
  const order = [...t.elements].reverse();
  /** Drag a row to restack it: pointer events, so a mouse, a pen and a finger all work. The drag
   *  starts after a 4 px move, so a plain click still selects. */
  const rowDrag = useRef<{ id: string; y: number; on: boolean; pid: number } | null>(null);
  const slotAt = (clientY: number) => {
    const els = rows.current ? Array.from(rows.current.querySelectorAll<HTMLElement>('[data-layer]')) : [];
    const k = els.findIndex((r) => { const b = r.getBoundingClientRect(); return clientY < b.top + b.height / 2; });
    return k < 0 ? els.length : k;
  };
  const rowDown = (ev: RPointerEvent<HTMLDivElement>, e: Element) => {
    if (ev.button !== 0 || frozen(e) || renaming === e.id || (ev.target as HTMLElement).closest('button,input')) return;
    rowDrag.current = { id: e.id, y: ev.clientY, on: false, pid: ev.pointerId };
  };
  const rowMove = (ev: RPointerEvent<HTMLDivElement>) => {
    const d = rowDrag.current; if (!d) return;
    if (!d.on) {
      if (Math.abs(ev.clientY - d.y) < 4) return;
      d.on = true; setDragId(d.id);
      try { (ev.currentTarget as HTMLElement).setPointerCapture(d.pid); } catch { /* fine */ }
    }
    ev.preventDefault();
    setDropAt(slotAt(ev.clientY));
  };
  const rowUp = (ev: RPointerEvent<HTMLDivElement>) => {
    const d = rowDrag.current; rowDrag.current = null;
    if (!d || !d.on) return;
    const at = slotAt(ev.clientY);
    const from = order.findIndex((x) => x.id === d.id);
    // display slot -> index in the list without the dragged layer (0 = back)
    const slot = at > from ? at - 1 : at;
    if (from >= 0 && slot !== from) restack(d.id, t.elements.length - 1 - slot);
    setDragId(null); setDropAt(null);
    // the click that follows a drag must not change the selection
    const stop = (c: MouseEvent) => { c.stopPropagation(); c.preventDefault(); };
    window.addEventListener('click', stop, { capture: true, once: true });
    setTimeout(() => window.removeEventListener('click', stop, { capture: true }), 50);
  };
  const clickRow = (ev: RMouseEvent, e: Element) => {
    if (!pickable(e) && !readOnly && !e.hidden) { deny(); return; }
    if (ev.shiftKey) setSels((s) => (s.includes(e.id) ? s.filter((x) => x !== e.id) : [...s.filter((x) => x !== BG_LAYER), e.id]));
    else setSel(e.id);
  };
  const rowKey = (ev: RKeyboardEvent<HTMLDivElement>, e: Element, i: number) => {
    if ((ev.target as HTMLElement).tagName === 'INPUT') return;
    const focusRow = (k: number) => (rows.current?.querySelectorAll<HTMLElement>('[data-layer-row]')[k])?.focus();
    if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); if (!frozen(e) || readOnly) setSel(e.id); return; }
    if (ev.key === 'F2') { ev.preventDefault(); if (!frozen(e)) setRenaming(e.id); return; }
    if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
    ev.preventDefault(); ev.stopPropagation();          // the window handler would nudge the box instead
    const up = ev.key === 'ArrowUp';
    if (ev.altKey) {
      restack(e.id, ev.shiftKey ? (up ? 'front' : 'back') : (up ? 'up' : 'down'));
      requestAnimationFrame(() => focusRow(ev.shiftKey ? (up ? 0 : order.length - 1) : Math.max(0, Math.min(order.length - 1, i + (up ? -1 : 1)))));
    } else focusRow(Math.max(0, Math.min(order.length, i + (up ? -1 : 1))));
  };
  const rename = (id: string, name: string) => {
    setRenaming(null);
    const e = t.elements.find((x) => x.id === id); if (!e || frozen(e) || (e.name ?? '') === name.trim()) return;
    edit((tt) => { const x = tt.elements.find((q) => q.id === id); if (x) x.name = name.trim().slice(0, 60); });
  };

  const rowBtn = 'p-1 rounded hover:bg-white/10 disabled:opacity-25 disabled:hover:bg-transparent';
  const canRestack = !readOnly && movable().length > 0;
  const rotatable = !!el && ROTATES.includes(el.role);
  const lead = multi ? selected[selected.length - 1] : el;

  return (
    <div>
      {banner}
      <div className={cn('grid grid-cols-1 gap-4', wide ? '' : 'xl:grid-cols-[minmax(0,1fr)_420px]')}>
        <Card>
          {/* toolbar */}
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-1 flex-wrap">
              {(['en', 'ar'] as const).map((l) => (
                <button key={l} onClick={() => setLang(l)} className={cn('px-3 h-8 rounded text-xs', lang === l ? 'bg-accent-500/20 text-white' : 'text-muted')}>
                  {l === 'en' ? 'English' : 'العربية'}
                </button>
              ))}
              <span className="w-px h-6 bg-slate-800 mx-1" />
              <IconBtn title="Undo (Ctrl+Z)" onClick={undo} disabled={readOnly || !past.current.length} testid="undo"><Undo2 size={15} /></IconBtn>
              <IconBtn title="Redo (Ctrl+Shift+Z)" onClick={redo} disabled={readOnly || !future.current.length} testid="redo"><Redo2 size={15} /></IconBtn>
              <span className="w-px h-6 bg-slate-800 mx-1" />
              <IconBtn title="Bring to front (Ctrl+Shift+])" onClick={() => restackSel('front')} disabled={!canRestack} testid="tool-front"><ChevronsUp size={15} /></IconBtn>
              <IconBtn title="Bring forward (Ctrl+])" onClick={() => restackSel('up')} disabled={!canRestack} testid="tool-up"><ChevronUp size={15} /></IconBtn>
              <IconBtn title="Send backward (Ctrl+[)" onClick={() => restackSel('down')} disabled={!canRestack} testid="tool-down"><ChevronDown size={15} /></IconBtn>
              <IconBtn title="Send to back, just above the background (Ctrl+Shift+[)" onClick={() => restackSel('back')} disabled={!canRestack} testid="tool-back"><ChevronsDown size={15} /></IconBtn>
              <IconBtn title="Select the layer below (or Alt+click on the design)" onClick={selectBelow} testid="select-below"><LayersIcon size={15} /></IconBtn>
              <span className="w-px h-6 bg-slate-800 mx-1" />
              <IconBtn title="Snap to edges, centre and other layers (hold Alt while dragging to move freely)" onClick={() => setSnap(!snap)} active={snap}><Magnet size={15} /></IconBtn>
              <IconBtn title="Layout grid and margins" onClick={() => setGrid(!grid)} active={grid} testid="toggle-grid"><Grid3x3 size={15} /></IconBtn>
              <IconBtn title="Instagram safe zones" onClick={() => setSafe(!safe)} active={safe}><Smartphone size={15} /></IconBtn>
              <IconBtn title="Bigger canvas" onClick={() => setWide(!wide)} active={wide}><Maximize2 size={15} /></IconBtn>
              {!readOnly && <IconBtn title="Paste layers (Ctrl+V)" onClick={paste} testid="paste"><ClipboardPaste size={15} /></IconBtn>}
            </div>
            {!readOnly && (
              <div className="flex flex-wrap gap-1">
                {ROLES.map((r) => (
                  <button key={r} onClick={() => add(r)}
                    disabled={!canLock && roleRule(kit, r) === 'brand'} title={!canLock && roleRule(kit, r) === 'brand' ? BRAND_TIP : undefined}
                    className="px-2 h-7 rounded border border-slate-800 text-xs text-muted hover:text-white disabled:opacity-30 disabled:hover:text-muted" data-testid={`add-${r}`}>
                    + {ROLE_LABEL[r]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative mx-auto select-none touch-none" style={{ maxWidth: wide ? (story ? 720 : 1100) : (story ? 520 : 760) }}
            ref={wrap} onPointerDown={onCanvasDown} onPointerMove={onCanvasMove} onPointerUp={onCanvasUp} onPointerCancel={onCanvasUp} data-testid="canvas-wrap">
            <DesignCanvas clientId={clientId} kit={kit} template={t} lang={lang} texts={preview.texts} images={preview.images}
              canvasRef={live} onReady={(ok) => { if (ok && live.current) { try { setThumb(live.current.toDataURL('image/jpeg', 0.7)); } catch { /* fine */ } } }}
              onSizes={(s) => setSizes((o) => (JSON.stringify(o) === JSON.stringify(s) ? o : s))}
              overlay={bgSel && bgFrame ? (
                <PhotoFrames w={t.w} h={t.h} frames={[bgFrame]} crops={{ [BG_LAYER]: bgCropNow }} sizes={sizes} selected={BG_LAYER}
                  onSelect={(id) => { if (!id) setSel(null); }} onPick={(_, f) => bgUpload(f)} onCrop={(_, c) => setBgCrop(c)} />
              ) : undefined} />
            {/* overlays: grid, margins, safe zones, snap guides */}
            <div className="absolute inset-0 pointer-events-none">
              {grid && (
                <>
                  <div className="absolute border border-dashed border-cyan-400/60" style={{ left: pct(MARGIN, t.w), right: pct(MARGIN, t.w), top: pct(MARGIN, t.h), bottom: pct(MARGIN, t.h) }} />
                  {Array.from({ length: 11 }, (_, i) => (
                    <div key={i} className="absolute top-0 bottom-0 w-px bg-cyan-400/15" style={{ left: pct(MARGIN + ((t.w - 2 * MARGIN) / 12) * (i + 1), t.w) }} />
                  ))}
                  <div className="absolute top-0 bottom-0 w-px bg-cyan-300/50" style={{ left: '50%' }} />
                  <div className="absolute left-0 right-0 h-px bg-cyan-300/50" style={{ top: '50%' }} />
                </>
              )}
              {safe && story && (
                <>
                  <div className="absolute left-0 right-0 top-0 bg-rose-500/15 border-b border-rose-400/50" style={{ height: pct(270, t.h) }}><span className="text-[10px] text-rose-200 px-1">Reel: account name, progress bar</span></div>
                  <div className="absolute left-0 right-0 bottom-0 bg-rose-500/15 border-t border-rose-400/50" style={{ height: pct(670, t.h) }}><span className="text-[10px] text-rose-200 px-1">Reel: caption, audio, buttons</span></div>
                  <div className="absolute right-0 bottom-0 bg-rose-500/10 border-l border-rose-400/40" style={{ width: pct(230, t.w), height: pct(t.h * 0.4, t.h) }} />
                  <div className="absolute left-0 right-0 border-y border-dashed border-amber-300/70" style={{ top: pct(240, t.h), bottom: pct(240, t.h) }}>
                    <span className="absolute top-1 left-1 text-[10px] text-amber-200 bg-black/40 px-1 rounded">profile grid shows this band (3:4)</span>
                  </div>
                </>
              )}
              {safe && !story && t.h > t.w && (
                // the profile grid shows a feed post cropped to 3:4, centred
                <div className="absolute top-0 bottom-0 border-x border-dashed border-amber-300/60" style={{ left: pct((t.w - t.h * 0.75) / 2, t.w), right: pct((t.w - t.h * 0.75) / 2, t.w) }}>
                  <span className="absolute top-1 left-1 text-[10px] text-amber-200 bg-black/40 px-1 rounded">profile grid crop</span>
                </div>
              )}
              {guides.map((g, i) => g.axis === 'x'
                ? <div key={i} className="absolute top-0 bottom-0 w-px bg-fuchsia-500" style={{ left: pct(flip ? t.w - g.at : g.at, t.w) }} />
                : <div key={i} className="absolute left-0 right-0 h-px bg-fuchsia-500" style={{ top: pct(g.at, t.h) }} />)}
            </div>
            {/* the layer outlines: the canvas wrap does the hit test (top-most first); these only show,
                except the handles of the one selected layer */}
            {!bgSel && t.elements.map((e) => e.hidden ? null : (
              <div key={e.id} data-testid="box" data-box={e.id}
                className={cn('absolute pointer-events-none border',
                  sels.includes(e.id) ? 'border-accent-400 bg-accent-400/10' : 'border-dashed border-white/25')}
                style={{ left: pct(viewX(e), t.w), top: pct(e.y, t.h), width: pct(e.w, t.w), height: pct(e.h, t.h),
                  transform: e.rotation && ROTATES.includes(e.role) ? `rotate(${e.rotation}deg)` : undefined }}>
                {frozen(e) && !readOnly && <span className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/60 text-white" title={BRAND_TIP} data-testid="box-locked"><Lock size={10} /></span>}
                {sels.includes(e.id) && (
                  <span className="absolute -top-5 left-0 text-[10px] text-white bg-sky-600/90 px-1.5 rounded whitespace-nowrap" data-testid="layer-chip">
                    {layerName(e)}{e.id === sel && !multi ? ` · ${e.w} x ${e.h}` : ''}
                  </span>
                )}
                {e.id === sel && !multi && !frozen(e) && (
                  <>
                    {HANDLES.map((h) => (
                      <span key={h} onPointerDown={(ev) => { ev.stopPropagation(); ev.preventDefault(); startDrag(ev, 'size', e.id, h); }} data-testid={`handle-${h}`}
                        className="absolute w-3 h-3 -ml-1.5 -mt-1.5 bg-white border border-sky-500 rounded-sm pointer-events-auto"
                        style={{ left: `${HANDLE_POS[h][0]}%`, top: `${HANDLE_POS[h][1]}%`, cursor: CURSOR[h] }} />
                    ))}
                    {ROTATES.includes(e.role) && (
                      <span onPointerDown={(ev) => { ev.stopPropagation(); ev.preventDefault(); startDrag(ev, 'rotate', e.id); }} title="Drag to rotate (up to 45 degrees)" data-testid="handle-rotate"
                        className="absolute left-1/2 -top-8 w-3.5 h-3.5 -ml-[7px] rounded-full bg-white border border-sky-500 pointer-events-auto cursor-grab" />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          {bgSel && bgFrame && bgFrame.adjust ? (
            <FrameToolbar frame={bgFrame} crop={bgCropNow} size={sizes[BG_LAYER]} linked={false}
              onCrop={setBgCrop} onDone={() => setSel(null)}
              onReplace={() => document.querySelector<HTMLInputElement>('[data-testid="bg-upload"]')?.click()} />
          ) : (
            <p className="text-[11px] text-muted mt-3 text-center">
              {readOnly ? SHARED_TIP
                : 'Click selects the top layer, Alt+click the one beneath, Shift+click adds. Drag to move; handles resize (Shift keeps the shape) and rotate. Arrows nudge (Shift = 10 px). Ctrl+] / Ctrl+[ restack, Ctrl+C / Ctrl+V copy and paste, Ctrl+D duplicates.'}
            </p>
          )}
        </Card>

        <div className="flex flex-col gap-4">
          {/* layers: front at the top, the background always at the bottom (owner 2026-10-24) */}
          <Card data-testid="layers">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="font-medium text-muted-strong">Layers</h3>
              <span className="text-[11px] text-muted">top = front</span>
            </div>
            {lead && (
              <div className="grid grid-cols-[1fr_auto] gap-2 items-end mb-2 pb-2 border-b border-slate-800" data-testid="layer-quick">
                <fieldset disabled={!canRestack} className="contents">
                  <Slider label={multi ? `Opacity (${selected.length} layers)` : 'Opacity'} value={lead.opacity ?? 1}
                    onChange={(v) => upd({ opacity: v }, gesture())} testid="layer-opacity" title="How see-through the layer is" />
                  <div className="w-32"><BlendSelect compact value={(lead.blend ?? '') as Blend} onChange={(v) => upd({ blend: v })} testid="layer-blend" /></div>
                </fieldset>
              </div>
            )}
            <div className="flex flex-col gap-0.5 max-h-[26rem] overflow-auto" ref={rows} role="listbox" aria-label="Layers, front first" aria-multiselectable
>
              {order.map((e, i) => {
                const fz = frozen(e);
                const on = sels.includes(e.id);
                return (
                  <div key={e.id} data-testid="layer" data-layer-row data-layer={e.id} tabIndex={0} role="option" aria-selected={on}
                    onPointerDown={(ev) => rowDown(ev, e)} onPointerMove={rowMove} onPointerUp={rowUp} onPointerCancel={() => { rowDrag.current = null; setDragId(null); setDropAt(null); }}
                    onClick={(ev) => clickRow(ev, e)} onDoubleClick={() => { if (!fz) setRenaming(e.id); }} onKeyDown={(ev) => rowKey(ev, e, i)}
                    title={fz && !readOnly ? BRAND_TIP : 'Drag to restack. Double-click to rename.'}
                    className={cn('relative flex items-center gap-1 pl-0.5 pr-1 min-h-10 rounded cursor-pointer text-sm outline-none focus-visible:ring-1 focus-visible:ring-accent-400',
                      on ? 'bg-accent-500/15 text-muted-strong ring-1 ring-accent-400/60' : 'text-muted hover:bg-white/5', fz && !readOnly && 'cursor-not-allowed',
                      e.hidden && 'opacity-50', dragId === e.id && 'opacity-40')}>
                    {dropAt === i && <span className="absolute -top-px left-0 right-0 h-0.5 bg-accent-400 rounded" data-testid="layer-drop-line" />}
                    {dropAt === i + 1 && i === order.length - 1 && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-accent-400 rounded" />}
                    <span className="touch-none self-stretch inline-flex items-center px-0.5" data-testid="layer-grip"><GripVertical size={14} className={cn('shrink-0', fz ? 'opacity-10' : 'opacity-50 cursor-grab')} aria-hidden /></span>
                    <Thumb kit={kit} e={e} lang={lang} />
                    {fz && !readOnly && <Lock size={12} className="text-amber-300 shrink-0" aria-label={BRAND_TIP} data-testid="layer-brand" />}
                    <span className="flex-1 min-w-0 flex flex-col leading-tight">
                      {renaming === e.id ? (
                        <input autoFocus defaultValue={e.name ?? ''} placeholder={ROLE_LABEL[e.role]} data-testid="layer-rename" onClick={(ev) => ev.stopPropagation()}
                          onBlur={(ev) => rename(e.id, ev.target.value)} onKeyDown={(ev) => { ev.stopPropagation(); if (ev.key === 'Enter') rename(e.id, (ev.target as HTMLInputElement).value); if (ev.key === 'Escape') setRenaming(null); }}
                          className="h-7 rounded border border-accent-400 bg-bg px-2 text-sm text-muted-strong" />
                      ) : <span className="truncate" data-testid="layer-name">{layerName(e)}</span>}
                      <span className="text-[10px] text-muted truncate" data-testid="layer-meta">
                        {Math.round((e.opacity ?? 1) * 100)}%{e.blend ? ` · ${BLENDS.find((b) => b.value === e.blend)?.label}` : ''}{e.fade ? ' · faded' : ''}
                        {e.role !== 'icon' && !IMAGE_ROLES.includes(e.role) && e.role !== 'shape' && preview.texts[e.id] && !e.name ? ` · ${preview.texts[e.id].slice(0, 18)}` : ''}
                      </span>
                    </span>
                    {on && !multi && !fz && (
                      <span className="flex" data-testid="layer-row-tools">
                        <button className={rowBtn} disabled={i === 0} title="Bring to front" aria-label="Bring to front" data-testid="layer-front" onClick={(ev) => { ev.stopPropagation(); restack(e.id, 'front'); }}><ChevronsUp size={14} /></button>
                        <button className={rowBtn} disabled={i === 0} title="Bring forward" aria-label="Bring forward" data-testid="layer-up" onClick={(ev) => { ev.stopPropagation(); restack(e.id, 'up'); }}><ChevronUp size={14} /></button>
                        <button className={rowBtn} disabled={i === order.length - 1} title="Send backward" aria-label="Send backward" data-testid="layer-down" onClick={(ev) => { ev.stopPropagation(); restack(e.id, 'down'); }}><ChevronDown size={14} /></button>
                        <button className={rowBtn} disabled={i === order.length - 1} title="Send to back" aria-label="Send to back" data-testid="layer-back" onClick={(ev) => { ev.stopPropagation(); restack(e.id, 'back'); }}><ChevronsDown size={14} /></button>
                      </span>
                    )}
                    <button className={rowBtn} disabled={fz} title={e.pinned ? 'Locked in place: click to unlock' : 'Lock in place'} aria-label={e.pinned ? 'Unlock position' : 'Lock position'} data-testid="layer-lock"
                      onClick={(ev) => { ev.stopPropagation(); edit((tt) => { const x = tt.elements.find((q) => q.id === e.id)!; x.pinned = !x.pinned; }); }}>
                      {e.pinned ? <Lock size={14} /> : <Unlock size={14} className="opacity-40" />}
                    </button>
                    <button className={rowBtn} disabled={fz} title={e.hidden ? 'Show' : 'Hide'} aria-label={e.hidden ? 'Show' : 'Hide'} data-testid="layer-eye"
                      onClick={(ev) => { ev.stopPropagation(); edit((tt) => { const x = tt.elements.find((q) => q.id === e.id)!; x.hidden = !x.hidden; }); }}>
                      {e.hidden ? <EyeOff size={14} /> : <Eye size={14} className="opacity-60" />}
                    </button>
                  </div>
                );
              })}
              {t.elements.length === 0 && <p className="text-sm text-muted py-2">No layers yet. Add them with the buttons above the design.</p>}
              {/* the background: always the bottom row; dropping on it sends a layer to the back */}
              <div data-testid="layer-bg" data-layer-row tabIndex={0} role="option" aria-selected={bgSel}
                onClick={() => setSel(BG_LAYER)} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); setSel(BG_LAYER); } }}
                className={cn('relative flex items-center gap-1.5 pl-2 h-10 mt-1 rounded border border-dashed cursor-pointer text-sm outline-none focus-visible:ring-1 focus-visible:ring-accent-400',
                  bgSel ? 'border-accent-400 bg-accent-500/15 text-muted-strong font-medium' : 'border-slate-700 text-muted hover:bg-white/5')}>
                {dropAt === order.length && order.length > 0 && <span className="absolute -top-1 left-0 right-0 h-0.5 bg-accent-400 rounded" />}
                <span className="w-6 h-6 rounded border border-white/15 shrink-0 inline-flex items-center justify-center" style={{ background: colourOf(kit, t.background_colour || 'background') }}>
                  {t.background && <ImageIcon size={13} className="text-black/60" />}
                </span>
                {bgFrozen && !readOnly && <Lock size={12} className="text-amber-300 shrink-0" aria-label={BRAND_TIP} data-testid="layer-bg-brand" />}
                <span className="flex-1 min-w-0 flex flex-col leading-tight">
                  <span className="truncate">Background</span>
                  <span className="text-[10px] text-muted">{t.background ? `photo ${Math.round((t.bg_opacity ?? 1) * 100)}%${t.bg_fade ? ' · faded' : ''}` : 'colour'} · always at the back</span>
                </span>
              </div>
            </div>
            <p className="text-[11px] text-muted mt-2">Drag rows to restack. Click a row to select a layer, even a hidden or covered one; Shift+click selects several.</p>
          </Card>

          {bgSel ? (
            <Card data-testid="bg-panel">
              {bgFrozen && !readOnly && <p className="mb-2 text-xs text-amber-300 flex items-center gap-1"><Lock size={12} /> {BRAND_TIP}</p>}
              <fieldset disabled={bgFrozen} className="min-w-0 border-0 p-0 m-0">
                <h3 className="font-medium text-muted-strong mb-3">Background</h3>
                <Section title="Photo" testid="bg-photo" more={t.background ? (
                  <>
                    <Slider label="Blur" value={t.bg_blur ?? 0} min={0} max={60} step={1} unit=" px" onChange={(v) => updBg({ bg_blur: Math.round(v) }, gesture())} testid="bg-blur" />
                    <Slider label="Zoom" value={t.bg_scale ?? 1} min={1} max={5} unit="x" onChange={(v) => setBgCrop({ ...bgCropNow, zoom: v })} testid="bg-zoom" />
                    <Slider label="Left, right" value={((t.bg_offset_x ?? 0) + 1) / 2} onChange={(v) => setBgCrop({ ...bgCropNow, x: v * 2 - 1 })} testid="bg-x" />
                    <Slider label="Up, down" value={((t.bg_offset_y ?? 0) + 1) / 2} onChange={(v) => setBgCrop({ ...bgCropNow, y: v * 2 - 1 })} testid="bg-y" />
                    <button className="justify-self-start text-xs text-muted hover:text-white" onClick={() => updBg({ bg_scale: 1, bg_offset_x: 0, bg_offset_y: 0 })}>Reset position</button>
                  </>
                ) : undefined}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className={cn('inline-flex items-center gap-1 text-sm text-accent-400 hover:underline', bgFrozen ? 'opacity-40' : 'cursor-pointer')}>
                      <Upload size={14} /> {busy ? 'Uploading...' : t.background ? 'Replace photo' : 'Add a background photo'}
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy || bgFrozen} data-testid="bg-upload"
                        onChange={(e) => { bgUpload(e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                    {t.background && <button className="text-xs text-muted hover:text-red-400" data-testid="bg-remove" onClick={() => updBg({ background: '' })}>Remove photo</button>}
                  </div>
                  {t.background && <p className="text-[11px] text-muted mt-1">Drag the photo on the design to move it; wheel or pinch to zoom.</p>}
                </Section>
                {t.background && (
                  <Section title="Opacity">
                    <Slider label="Photo opacity (fades into the colour)" value={t.bg_opacity ?? 1} onChange={(v) => updBg({ bg_opacity: v }, gesture())} testid="bg-opacity" />
                  </Section>
                )}
                {t.background && (
                  <FadeControls testid="bg-fade" fade={t.bg_fade ?? ''} side={t.bg_fade_side ?? 'bottom'} start={t.bg_fade_start ?? 1} end={t.bg_fade_end ?? 0}
                    onChange={(p) => updBg({ ...(p.fade !== undefined ? { bg_fade: p.fade } : {}), ...(p.side ? { bg_fade_side: p.side } : {}),
                      ...(p.start !== undefined ? { bg_fade_start: p.start } : {}), ...(p.end !== undefined ? { bg_fade_end: p.end } : {}) }, p.fade !== undefined || !!p.side || gesture())} />
                )}
                <Section title="Colour" more={
                  <>
                    <Select label="Colour fades to" value={t.bg_gradient_to ?? ''} onChange={(v) => updBg({ bg_gradient_to: v as Template['bg_gradient_to'] })} options={tokenOpts('solid colour')} />
                    {t.bg_gradient_to && <Select label="Fade type" value={t.bg_gradient_type ?? 'linear'} onChange={(v) => updBg({ bg_gradient_type: v as Template['bg_gradient_type'] })}
                      options={[{ value: 'linear', label: 'Linear' }, { value: 'radial', label: 'Radial glow' }]} />}
                    {t.bg_gradient_to && t.bg_gradient_type !== 'radial' && <Num label="Fade angle" value={t.bg_gradient_angle ?? 180} onChange={(v) => updBg({ bg_gradient_angle: ((v % 360) + 360) % 360 })} />}
                    <Slider label="Film grain" value={t.bg_grain ?? 0} onChange={(v) => updBg({ bg_grain: v }, gesture())} />
                  </>
                }>
                  <div className="grid grid-cols-2 gap-2">
                    <Select label="Colour" value={t.background_colour} onChange={(v) => updBg({ background_colour: v as Template['background_colour'] })} options={tokenOpts()} />
                    {t.background && <Select label="Colour wash on the photo" value={t.bg_tint ?? ''} onChange={(v) => updBg({ bg_tint: v as Template['bg_tint'], bg_tint_opacity: v ? (t.bg_tint_opacity || 0.35) : 0 })} options={tokenOpts('none')} />}
                    {t.background && t.bg_tint && <Slider label="Wash strength" value={t.bg_tint_opacity ?? 0} onChange={(v) => updBg({ bg_tint_opacity: v }, gesture())} testid="bg-wash" className="col-span-2" />}
                  </div>
                </Section>
              </fieldset>
            </Card>
          ) : multi ? (
            <Card data-testid="multi-panel">
              <h3 className="font-medium text-muted-strong mb-3">{selected.length} layers selected</h3>
              <fieldset disabled={!canRestack} className="min-w-0 border-0 p-0 m-0">
                <Section title="Position" testid="multi-position" more={
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => distribute('x')} disabled={selected.length < 3} data-testid="distribute-x">Space evenly across</Button>
                    <Button size="sm" variant="secondary" onClick={() => distribute('y')} disabled={selected.length < 3} data-testid="distribute-y">Space evenly down</Button>
                  </div>
                }>
                  <div className="flex gap-1" data-testid="align-tools">
                    <IconBtn title="Align left edges" onClick={() => align('l')} testid="align-l"><AlignStartVertical size={15} /></IconBtn>
                    <IconBtn title="Align centres" onClick={() => align('c')} testid="align-c"><AlignCenterVertical size={15} /></IconBtn>
                    <IconBtn title="Align right edges" onClick={() => align('r')}><AlignEndVertical size={15} /></IconBtn>
                    <span className="w-px h-6 bg-slate-800 mx-1 self-center" />
                    <IconBtn title="Align tops" onClick={() => align('t')}><AlignStartHorizontal size={15} /></IconBtn>
                    <IconBtn title="Align middles" onClick={() => align('m')}><AlignCenterHorizontal size={15} /></IconBtn>
                    <IconBtn title="Align bottoms" onClick={() => align('b')}><AlignEndHorizontal size={15} /></IconBtn>
                  </div>
                  <p className="text-[11px] text-muted mt-1">Drag any of them on the design to move them together.</p>
                </Section>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="secondary" icon={<Copy size={14} />} onClick={duplicate}>Duplicate</Button>
                  <Button size="sm" variant="secondary" icon={<Trash2 size={14} />} onClick={remove} data-testid="multi-delete">Delete</Button>
                </div>
              </fieldset>
            </Card>
          ) : el ? (
            <Card data-testid="box-panel">
              {elFrozen && !readOnly && <p className="mb-2 text-xs text-amber-300 flex items-center gap-1" data-testid="box-locked-note"><Lock size={12} /> {BRAND_TIP}</p>}
              <fieldset disabled={elFrozen} className="min-w-0 border-0 p-0 m-0">
              <div className="flex items-end gap-2 mb-3">
                <div className="flex-1"><Input label={`Layer name (${ROLE_LABEL[el.role]})`} value={el.name ?? ''} placeholder={ROLE_LABEL[el.role]} onChange={(e) => upd({ name: e.target.value.slice(0, 60) }, gesture())} data-testid="box-name" /></div>
                <IconBtn title="Duplicate (Ctrl+D)" onClick={duplicate}><Copy size={14} /></IconBtn>
                {/* labelled, not a bare icon: on a phone there is no Delete key to fall back on */}
                <button onClick={remove} data-testid="box-delete"
                  className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded border border-red-500/40 text-xs text-red-400 hover:bg-red-500/10">
                  <Trash2 size={14} /> Delete
                </button>
              </div>

              <Section title="Position & size" testid="sec-position" more={
                <>
                  <div className="grid grid-cols-4 gap-2">
                    {(['x', 'y', 'w', 'h'] as const).map((k) => <Num key={k} label={k.toUpperCase()} value={el[k]} onChange={(v) => upd({ [k]: v })} />)}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {rotatable && <Num label="Rotation (degrees)" value={el.rotation ?? 0} onChange={(v) => upd({ rotation: Math.max(-45, Math.min(45, v)) })} />}
                    {(!isText || el.role === 'cta') && el.role !== 'icon' && <Num label="Corner radius" value={el.radius} onChange={(v) => upd({ radius: v })} />}
                  </div>
                  {t.mirror_ar !== false && (
                    <label className="flex items-center gap-2 text-sm text-muted-strong">
                      <input type="checkbox" checked={!!el.keep_side} onChange={(e) => upd({ keep_side: e.target.checked })} data-testid="box-keep-side" />
                      Keep its side in the Arabic version
                    </label>
                  )}
                  {canLock && !readOnly && (
                    <div data-testid="box-asset">
                      {/* choosing here clears the old `locked` flag, so the choice is the only rule left */}
                      <Select label="This element" value={el.asset || (el.locked ? 'brand' : '')} onChange={(v) => upd({ asset: v as Element['asset'], locked: false })}
                        options={[{ value: '', label: `Follow the brand setting (${roleRule(kit, el.role) === 'brand' ? 'brand asset' : 'flexible'})` },
                          { value: 'brand', label: 'Brand asset (managers only)' }, { value: 'flexible', label: 'Flexible (team can change)' }]} />
                    </div>
                  )}
                </>
              }>
                <div className="flex gap-1" data-testid="align-tools">
                  <IconBtn title="Align left (margin)" onClick={() => align('l')} testid="align-l"><AlignStartVertical size={15} /></IconBtn>
                  <IconBtn title="Centre horizontally" onClick={() => align('c')} testid="align-c"><AlignCenterVertical size={15} /></IconBtn>
                  <IconBtn title="Align right (margin)" onClick={() => align('r')}><AlignEndVertical size={15} /></IconBtn>
                  <span className="w-px h-6 bg-slate-800 mx-1 self-center" />
                  <IconBtn title="Align top (margin)" onClick={() => align('t')}><AlignStartHorizontal size={15} /></IconBtn>
                  <IconBtn title="Centre vertically" onClick={() => align('m')}><AlignCenterHorizontal size={15} /></IconBtn>
                  <IconBtn title="Align bottom (margin)" onClick={() => align('b')}><AlignEndHorizontal size={15} /></IconBtn>
                </div>
              </Section>

              <Section title="Opacity">
                <Slider label="Opacity" value={el.opacity ?? 1} onChange={(v) => upd({ opacity: v }, gesture())} testid="box-opacity" title="How see-through this layer is" />
              </Section>

              <FadeControls testid="box-fade" fade={el.fade ?? ''} side={el.fade_side ?? 'bottom'} start={el.fade_start ?? 1} end={el.fade_end ?? 0}
                onChange={(p) => upd({ ...(p.fade !== undefined ? { fade: p.fade } : {}), ...(p.side ? { fade_side: p.side } : {}),
                  ...(p.start !== undefined ? { fade_start: p.start } : {}), ...(p.end !== undefined ? { fade_end: p.end } : {}) }, p.fade !== undefined || !!p.side || gesture())} />

              <Section title="Blend mode">
                <BlendSelect value={(el.blend ?? '') as Blend} onChange={(v) => upd({ blend: v })} testid="box-blend" />
              </Section>

              {el.role === 'icon' && (
                <Section title="Icon and colour" testid="icon-panel" more={
                  <div className="grid grid-cols-2 gap-2">
                    {ICONS.find((i) => i.key === (el.icon || 'question'))?.glyph || el.icon === 'text' ? (
                      <Select label="Font" value={el.font} onChange={(v) => upd({ font: v as Element['font'] })}
                        options={FONT_SLOTS.filter((s) => s.startsWith('en')).map((s) => ({ value: s, label: FONT_LABEL[s].replace('English ', '') }))} />
                    ) : <Select label="Fill (line icons)" value={el.fill} onChange={(v) => upd({ fill: v as Element['fill'] })} options={tokenOpts('none, line only')} />}
                    <Num label={ICONS.find((i) => i.key === (el.icon || 'question'))?.glyph || el.icon === 'text' ? 'Weight' : 'Line weight'} step={100} value={el.weight} onChange={(v) => upd({ weight: Math.max(100, Math.min(900, v || 600)) })} />
                    <label className="flex items-center gap-2 text-sm text-muted-strong col-span-2"><input type="checkbox" checked={!!el.shadow} onChange={(e) => upd({ shadow: e.target.checked })} /> Soft shadow</label>
                  </div>
                }>
                  <div className="grid grid-cols-2 gap-2">
                    <Select label="Icon or symbol" value={el.icon || 'question'} onChange={(v) => upd({ icon: v })} data-testid="icon-pick"
                      options={ICONS.map((i) => ({ value: i.key, label: i.label, hint: i.glyph ? i.glyph.en : '' }))} />
                    <Select label="Colour" value={el.colour} onChange={(v) => upd({ colour: v as Element['colour'] })} options={tokenOpts('none')} data-testid="icon-colour" />
                    {el.icon === 'text' && <Input label="Symbol (English)" value={el.default_en} onChange={(e) => upd({ default_en: e.target.value })} data-testid="icon-text" />}
                    {el.icon === 'text' && <Input label="Symbol (Arabic)" dir="rtl" value={el.default_ar} onChange={(e) => upd({ default_ar: e.target.value })} />}
                  </div>
                  <p className="text-[11px] text-muted mt-1">It fills its box: drag a corner to make it bigger.</p>
                </Section>
              )}

              {el.role === 'shape' && (
                <Section title="Colour" more={
                  <div className="grid grid-cols-2 gap-2">
                    <Select label="Gradient to" value={el.gradient_to ?? ''} onChange={(v) => upd({ gradient_to: v as Element['gradient_to'] })} options={tokenOpts('no gradient')} />
                    {el.gradient_to && <Select label="Gradient type" value={el.gradient_type ?? 'linear'} onChange={(v) => upd({ gradient_type: v as Element['gradient_type'] })}
                      options={[{ value: 'linear', label: 'Linear' }, { value: 'radial', label: 'Radial (glow from the centre)' }]} />}
                    {el.gradient_to && el.gradient_type !== 'radial' && <Num label="Gradient angle" value={el.gradient_angle ?? 90} onChange={(v) => upd({ gradient_angle: ((v % 360) + 360) % 360 })} />}
                    <Select label="Border" value={el.stroke ?? ''} onChange={(v) => upd({ stroke: v as Element['stroke'], stroke_w: v ? (el.stroke_w || 4) : 0 })} options={tokenOpts('none')} />
                    {el.stroke && <Num label="Border width" value={el.stroke_w} onChange={(v) => upd({ stroke_w: v })} />}
                    <label className="flex items-center gap-2 text-sm text-muted-strong col-span-2"><input type="checkbox" checked={!!el.shadow} onChange={(e) => upd({ shadow: e.target.checked })} /> Soft drop shadow</label>
                  </div>
                }>
                  <Select label="Colour" value={el.fill} onChange={(v) => upd({ fill: v as Element['fill'] })} options={tokenOpts('none (frame only)')} />
                </Section>
              )}

              {IMAGE_ROLES.includes(el.role) && (
                <Section title="Colour" more={el.role === 'logo' ? undefined : (
                  <div className="grid grid-cols-2 gap-2">
                    <Select label="Fit" value={el.fit} onChange={(v) => upd({ fit: v as Element['fit'] })} options={[{ value: 'cover', label: 'Fill the box' }, { value: 'contain', label: 'Fit inside' }]} />
                    <Select label="Fade to colour (text over photo)" value={el.scrim ?? ''} onChange={(v) => upd({ scrim: v as Element['scrim'], scrim_strength: v ? (el.scrim_strength || 0.85) : 0 })} options={tokenOpts('none')} />
                    {el.scrim && <Select label="Colour fade from" value={el.scrim_side ?? 'bottom'} onChange={(v) => upd({ scrim_side: v as Element['scrim_side'] })}
                      options={[{ value: 'grid', label: 'Instagram grid haze' }, { value: 'bottom', label: 'Bottom' }, { value: 'top', label: 'Top' }, { value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }]} />}
                    {el.scrim && <Slider label="Colour fade strength" value={el.scrim_strength ?? 0} onChange={(v) => upd({ scrim_strength: v }, gesture())} />}
                    {el.scrim && <Slider label="Colour fade covers" value={el.scrim_size ?? 0.55} min={0.1} onChange={(v) => upd({ scrim_size: v }, gesture())} />}
                    {el.fit === 'cover' && <label className="col-span-2 flex items-center gap-2 text-xs text-muted-strong"><input type="checkbox" checked={!!el.shrink} onChange={(e) => upd({ shrink: e.target.checked })} data-testid="el-shrink" /> The photo may zoom out smaller than the box (white-background portraits)</label>}
                    <Slider label="Blur" value={el.img_blur ?? 0} min={0} max={60} step={1} unit=" px" onChange={(v) => upd({ img_blur: Math.round(v) }, gesture())} />
                    <Slider label="Brightness" value={el.img_brightness ?? 1} min={0.3} max={1.7} unit="x" onChange={(v) => upd({ img_brightness: v }, gesture())} />
                    <label className="flex items-center gap-2 text-sm text-muted-strong col-span-2"><input type="checkbox" checked={!!el.img_bw} onChange={(e) => upd({ img_bw: e.target.checked })} /> Black and white</label>
                    <Select label="Border" value={el.stroke ?? ''} onChange={(v) => upd({ stroke: v as Element['stroke'], stroke_w: v ? (el.stroke_w || 4) : 0 })} options={tokenOpts('none')} />
                    {el.stroke && <Num label="Border width" value={el.stroke_w} onChange={(v) => upd({ stroke_w: v })} />}
                    <label className="flex items-center gap-2 text-sm text-muted-strong col-span-2"><input type="checkbox" checked={!!el.shadow} onChange={(e) => upd({ shadow: e.target.checked })} /> Soft drop shadow</label>
                  </div>
                )}>
                  {el.role === 'logo' ? (
                    <Select label="Logo version" value={el.colour === 'white' ? 'white' : 'main'} onChange={(v) => upd({ colour: v === 'white' ? 'white' : 'text' })}
                      options={[{ value: 'main', label: 'Main' }, { value: 'white', label: 'White' }]} />
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Select label="Brand tint" value={el.tint ?? ''} onChange={(v) => upd({ tint: v as Element['tint'], tint_opacity: v ? (el.tint_opacity || 0.25) : 0 })} options={tokenOpts('none')} />
                      {el.tint ? <Slider label="Tint strength" value={el.tint_opacity ?? 0} onChange={(v) => upd({ tint_opacity: v }, gesture())} testid="box-tint" /> : <span />}
                    </div>
                  )}
                </Section>
              )}

              {isText && (
                <>
                  <Section title="Colour" more={
                    <div className="grid grid-cols-2 gap-2">
                      <Select label="Highlight bar behind lines" value={el.highlight ?? ''} onChange={(v) => upd({ highlight: v as Element['highlight'] })} options={tokenOpts('none')} />
                      <Select label="Colour of *marked* words" value={el.emph_colour ?? 'accent'} onChange={(v) => upd({ emph_colour: v as Element['emph_colour'] })} options={tokenOpts()} />
                      {el.role !== 'cta' && <Select label="Outline" value={el.stroke ?? ''} onChange={(v) => upd({ stroke: v as Element['stroke'], stroke_w: v ? (el.stroke_w || 3) : 0 })} options={tokenOpts('none')} />}
                      {el.role !== 'cta' && el.stroke && <Num label="Outline width" value={el.stroke_w} onChange={(v) => upd({ stroke_w: v })} />}
                      <label className="flex items-center gap-2 text-sm text-muted-strong col-span-2"><input type="checkbox" checked={!!el.shadow} onChange={(e) => upd({ shadow: e.target.checked })} /> Soft shadow (text on photos)</label>
                    </div>
                  }>
                    <div className="grid grid-cols-2 gap-2">
                      <Select label="Text colour" value={el.colour} onChange={(v) => upd({ colour: v as Element['colour'] })} options={tokenOpts('none (outline only)')} />
                      {(el.role === 'cta' || el.role === 'label') && <Select label={el.role === 'label' ? 'Pill colour' : 'Button colour'} value={el.fill} onChange={(v) => upd({ fill: v as Element['fill'] })} options={tokenOpts('none')} />}
                    </div>
                  </Section>
                  <Section title="Text" testid="sec-text" more={
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        <Num label="Weight" step={100} value={el.weight} onChange={(v) => upd({ weight: v || 400 })} />
                        <Num label="Max lines" value={el.max_lines} onChange={(v) => upd({ max_lines: v || 1 })} />
                        <Num label="Line height (0 = auto)" step={0.05} value={el.line_height} onChange={(v) => upd({ line_height: v })} />
                        <Num label="Letter spacing" step={0.5} value={el.tracking} onChange={(v) => upd({ tracking: v })} />
                      </div>
                      <Select label="Vertical" value={el.valign} onChange={(v) => upd({ valign: v as Element['valign'] })}
                        options={[{ value: 'top', label: 'Top' }, { value: 'middle', label: 'Middle' }, { value: 'bottom', label: 'Bottom' }]} />
                      <div className="flex flex-wrap gap-4 text-sm text-muted-strong">
                        <label className="flex items-center gap-2"><input type="checkbox" checked={!!el.upper} onChange={(e) => upd({ upper: e.target.checked })} /> All caps (English)</label>
                        <label className="flex items-center gap-2"><input type="checkbox" checked={!!el.emph_italic} onChange={(e) => upd({ emph_italic: e.target.checked })} /> *Marked* words in italic</label>
                      </div>
                      <p className="text-[11px] text-muted">Put *stars* around words to colour them, e.g. Glow *naturally*.</p>
                    </>
                  }>
                    <div className="grid gap-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Select label="Font (Arabic uses its twin)" value={el.font} onChange={(v) => upd({ font: v as Element['font'] })}
                          options={FONT_SLOTS.filter((s) => s.startsWith('en')).map((s) => ({ value: s, label: FONT_LABEL[s].replace('English ', '') }))} />
                        <Num label="Size (px)" value={el.size} onChange={(v) => upd({ size: v || 12 })} />
                      </div>
                      <Select label="Align" value={el.align} onChange={(v) => upd({ align: v as Element['align'] })}
                        options={[{ value: 'start', label: 'Start (left EN, right AR)' }, { value: 'center', label: 'Centre' }, { value: 'end', label: 'End' }]} />
                      <Input label="Fixed text (English), empty = filled at generation" value={el.default_en} onChange={(e) => upd({ default_en: e.target.value })} data-testid="box-text-en" />
                      <Input label="Fixed text (Arabic)" dir="rtl" value={el.default_ar} onChange={(e) => upd({ default_ar: e.target.value })} />
                    </div>
                  </Section>
                </>
              )}
              </fieldset>
            </Card>
          ) : (
            <Card><p className="text-sm text-muted">Click a layer on the design or in Layers to change it. The Background row holds the photo, its fade and its position.</p></Card>
          )}

          {thumb && (
            <Card data-testid="grid-preview">
              <h3 className="font-medium text-muted-strong mb-2">How it looks small</h3>
              <div className="flex items-end gap-4">
                <div>
                  <div className="overflow-hidden rounded-sm border border-slate-700" style={{ width: 120, height: 160 }}>
                    <img src={thumb} alt="" style={{ width: 120, height: (120 * t.h) / t.w, marginTop: -((120 * t.h) / t.w - 160) / 2 }} />
                  </div>
                  <div className="text-[10px] text-muted mt-1">profile grid</div>
                </div>
                <div>
                  <img src={thumb} alt="" className="rounded-sm border border-slate-700" style={{ width: 72 }} />
                  <div className="text-[10px] text-muted mt-1">Reels tab</div>
                </div>
                <p className="text-[11px] text-muted flex-1">If the title is hard to read here, cut words. Aim for 3 to 5.</p>
              </div>
            </Card>
          )}

          <Card>
            <h3 className="font-medium text-muted-strong mb-3">Template</h3>
            <fieldset disabled={readOnly} className="min-w-0 border-0 p-0 m-0 grid gap-3">
              <Input label="Name" value={t.name} onChange={(e) => updT({ name: e.target.value })} data-testid="template-name" />
              <Select label="Used for" value={t.purpose} onChange={(v) => updT({ purpose: v })} options={PURPOSES} />
              <Select label="Format" value={fmt} placeholder={`${t.w} x ${t.h}`} onChange={(v) => { const f = FORMATS.find((x) => x.key === v)!; updT({ w: f.w, h: f.h }); }}
                options={FORMATS.map((f) => ({ value: f.key, label: f.label }))} />
              {onAdapt && (
                <Select label="Adapt a copy to another format" value="" placeholder="Pick a format..." data-testid="adapt"
                  onChange={(v) => { const f = FORMATS.find((x) => x.key === v)!; onAdapt(adaptTemplate(t, f.w, f.h, `${t.name} (${f.label.split(' (')[0]})`)); }}
                  options={FORMATS.filter((f) => f.key !== fmt).map((f) => ({ value: f.key, label: f.label }))} />
              )}
              <Select label="Languages" value={t.lang} onChange={(v) => updT({ lang: v as Template['lang'] })}
                options={[{ value: 'both', label: 'Arabic and English' }, { value: 'ar', label: 'Arabic only' }, { value: 'en', label: 'English only' }]} />
              <label className="flex items-center gap-2 text-sm text-muted-strong">
                <input type="checkbox" checked={t.mirror_ar !== false} onChange={(e) => updT({ mirror_ar: e.target.checked })} />
                Mirror the layout for Arabic (right to left)
              </label>
              <button className="justify-self-start text-sm text-accent-400 hover:underline" onClick={() => setSel(BG_LAYER)} data-testid="open-bg"><Square size={12} className="inline mr-1" />Background photo, colour and fade</button>
            </fieldset>
            {(onDuplicate || onDelete) && (
              <div className="flex gap-2 pt-3">
                {onDuplicate && <Button size="sm" variant="secondary" icon={<Copy size={14} />} onClick={() => onDuplicate(t)} data-testid="template-duplicate">Duplicate</Button>}
                {onDelete && <Button size="sm" variant="secondary" icon={<Trash2 size={14} />} onClick={onDelete} data-testid="template-delete">Delete</Button>}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
